import type { ComponentType } from 'react';
import AdoptionReportPage from '../pages/AdoptionReportPage';
import CensusReport from '../pages/CensusReport';
import LateStartsReport from '../pages/LateStartsReport';
import LupaRiskReport from '../pages/LupaRiskReport';
import MarketingReport from '../pages/MarketingReport';
import PatientDiagnosesReport from '../pages/PatientDiagnosesReport';
import PatientRosterReport from '../pages/PatientRosterReport';
import PastDueVisitsReport from '../pages/PastDueVisitsReport';
import PcrReport from '../pages/PcrReport';
import RecertificationTimingReport from '../pages/RecertificationTimingReport';
import ScheduleDeviationReport from '../pages/ScheduleDeviationReport';
import ScheduleReport from '../pages/ScheduleReport';
import UserOnboardingReport from '../pages/UserOnboardingReport';
import WorklogReport from '../pages/WorklogReport';
import WoundStatusReport from '../pages/WoundStatusReport';

export type ReportCardBadge = 'Documentation' | 'EMR';
export type ReportCardIcon = 'bar-chart' | 'file-bar-chart';

export interface ReportCatalogEntry {
  slug: string;
  name: string;
  description: string;
  modernPath: `/reports/${string}`;
  legacyPath?: `/reporting/${string}`;
  component: ComponentType;
  badge: ReportCardBadge;
  icon: ReportCardIcon;
}

const standardReport = (
  entry: Omit<ReportCatalogEntry, 'modernPath' | 'legacyPath'> & {
    slug: string;
  }
): ReportCatalogEntry => ({
  ...entry,
  modernPath: `/reports/${entry.slug}`,
  legacyPath: `/reporting/${entry.slug}`,
});

export const reportCatalog: readonly ReportCatalogEntry[] = [
  standardReport({
    slug: 'adoption',
    name: 'Clinician Adoption',
    description: 'Documentation usage metrics',
    component: AdoptionReportPage,
    badge: 'Documentation',
    icon: 'bar-chart',
  }),
  standardReport({
    slug: 'census',
    name: 'Census Report',
    description: 'Current patients and episode census rows',
    component: CensusReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'patient-diagnoses',
    name: 'Patient Diagnoses',
    description:
      'Review active patients by diagnosis, payer, and HIPPS details',
    component: PatientDiagnosesReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  {
    slug: 'pcr',
    name: 'PCR Report',
    description: 'Review Pre-Claim Review tracking for RCD episodes',
    modernPath: '/reports/pcr',
    component: PcrReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  },
  standardReport({
    slug: 'patient-roster',
    name: 'Patient Roster',
    description:
      'Weekly patient roster with triage, disaster, and diagnosis codes',
    component: PatientRosterReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'schedule',
    name: 'Schedule Report',
    description: 'Review scheduled visits by date, clinician, and status',
    component: ScheduleReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'schedule-deviation',
    name: 'Schedule Deviation',
    description: 'Find visits documented outside their planned schedule',
    component: ScheduleDeviationReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'late-starts',
    name: 'Late Starts',
    description: 'Find admissions where SOC followed the admission start date',
    component: LateStartsReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'worklog',
    name: 'Worklog Report',
    description: 'Review task worklog activity by user and date',
    component: WorklogReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'user-onboarding',
    name: 'User Onboarding Report',
    description: 'Review agency members, invite times, and task activity',
    component: UserOnboardingReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'past-due-visits',
    name: 'Past Due Tasks',
    description: 'Find overdue visits and orders',
    component: PastDueVisitsReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'marketing',
    name: 'Marketing Report',
    description: 'Referrals received by date, branch, and internal source',
    component: MarketingReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'wound-status',
    name: 'Wound Status Report',
    description: 'Review wound status, location, and progression by patient',
    component: WoundStatusReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'lupa-risk',
    name: 'LUPA Risk Report',
    description: 'Find Medicare episodes below their LUPA visit threshold',
    component: LupaRiskReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'recertification-timing',
    name: 'Recertification Timing',
    description:
      'Review recertifications completed on episode days 56 through 60',
    component: RecertificationTimingReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
];

export const pairedStandardReports = reportCatalog.filter(
  (
    report
  ): report is ReportCatalogEntry & { legacyPath: `/reporting/${string}` } =>
    report.legacyPath !== undefined
);
